// src/utils/bible/cache.js
// Handles fetching, caching (IndexedDB + localStorage fallback), and querying
// Bible data. Not meant to be imported directly by page components — use bibleService.js.
import axios from "../../services/axiosConfig";
import API_ENDPOINTS from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import { getBibleDataFromDB, getChaptersFromDB, getVerseFromDB, getVersesFromDB, isBibleDataCached, 
  isIndexedDBSupported, indexBibleVerses, initBibleDB, storeBibleData, storeBibleDataMain, searchVersesInDB } from "./indexedDB";
import { bookMatchesQuery, extractBookMetadata, getOrderedBooks } from "./metadata";

const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

const CACHE_KEYS = {
  data: (v) => `bible_${v}_data`,
  time: (v) => `bible_${v}_time`
};

// ─── Data normalisation ───────────────────────────────────────────────────────

function normalizeBibleData(raw, versionId) {
  if (!raw) return { metadata: { versionId }, verses: [] };

  if (raw?.verses && Array.isArray(raw.verses)) {
    return {
      ...raw,
      verses: raw.verses.map((v) => ({
        ...v,
        book_name: v.book_name || v.book,
        book: v.book
      }))
    };
  }

  if (Array.isArray(raw)) {
    return {
      metadata: { versionId },
      verses: raw.map((v) => ({
        ...v,
        book_name: v.book_name || v.book
      }))
    };
  }

  return { metadata: { versionId }, verses: [] };
}

// ─── API helpers ──────────────────────────────────────────────────────────────

export async function getAvailableBibleVersions() {
  try {
    const token = await getAuthToken();
    const response = await axios.get(API_ENDPOINTS.BIBLE_VERSIONS, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data?.data || [];
  } catch (error) {
    console.error("[bibleCache] getAvailableBibleVersions failed:", error);
    return [];
  }
}

async function fetchBibleData(versionId) {
  if (!versionId) throw new Error("[bibleCache] fetchBibleData: missing versionId");

  try {
    const token = await getAuthToken();
    const presignedRes = await axios.get(API_ENDPOINTS.BIBLE_PRESIGNED(versionId), { headers: { Authorization: `Bearer ${token}` } });

    const url = presignedRes.data?.data?.presignedUrl;
    if (!url) throw new Error("[bibleCache] No presigned URL in response");

    const bibleRes = await fetch(url);
    if (!bibleRes.ok) {
      throw new Error(`[bibleCache] Bible data fetch failed with status ${bibleRes.status}`);
    }

    const raw = await bibleRes.json();
    return normalizeBibleData(raw, versionId);
  } catch (error) {
    console.error("[bibleCache] fetchBibleData failed:", error);
    throw error;
  }
}

// ─── Main cache entry point ───────────────────────────────────────────────────

/**
 * Returns cached Bible data for a version, fetching from the network if
 * needed. Always pass an explicit versionId — there is no default.
 */
export async function getCachedBibleData(versionId) {
  if (!versionId) {
    console.error("[bibleCache] getCachedBibleData called without versionId");
    return null;
  }

  if (isIndexedDBSupported()) {
    try {
      await initBibleDB();
      const isCached = await isBibleDataCached(versionId, CACHE_TTL);

      if (isCached) {
        const cachedData = await getBibleDataFromDB(versionId);
        if (cachedData?.verses?.length > 0) {
          return normalizeBibleData(cachedData, versionId);
        }
      }

      const fresh = await fetchBibleData(versionId);
      await storeBibleDataMain(versionId, fresh);

      if (fresh?.verses?.length > 0) {
        indexBibleVerses(versionId, fresh.verses).catch((err) => {
          console.warn("[bibleCache] Background indexing failed:", err);
        });
      }

      try {
        localStorage.setItem(CACHE_KEYS.data(versionId), JSON.stringify(fresh));
        localStorage.setItem(CACHE_KEYS.time(versionId), Date.now().toString());
      } catch {
        // localStorage quota exceeded — IndexedDB already has it, so safe to ignore
      }

      return fresh;
    } catch (error) {
      console.error("[bibleCache] IndexedDB path failed, falling back to localStorage:", error);
    }
  }

  // ── localStorage fallback ──────────────────────────────────────────────────
  const key = CACHE_KEYS.data(versionId);
  const timeKey = CACHE_KEYS.time(versionId);
  const now = Date.now();

  try {
    const cached = localStorage.getItem(key);
    const time = localStorage.getItem(timeKey);

    if (cached && time && now - Number(time) < CACHE_TTL) {
      return normalizeBibleData(JSON.parse(cached), versionId);
    }

    const fresh = await fetchBibleData(versionId);

    try {
      localStorage.setItem(key, JSON.stringify(fresh));
      localStorage.setItem(timeKey, now.toString());
    } catch {
      console.warn("[bibleCache] localStorage quota exceeded, trying IndexedDB");
      if (isIndexedDBSupported()) {
        try {
          await initBibleDB();
          await storeBibleData(versionId, fresh);
        } catch (idbError) {
          console.error("[bibleCache] IndexedDB fallback also failed:", idbError);
        }
      }
    }

    return fresh;
  } catch (error) {
    console.error("[bibleCache] getCachedBibleData failed entirely:", error);
    return null;
  }
}

// ─── Search ───────────────────────────────────────────────────────────────────

/**
 * Searches Bible data for a term. Handles both reference-style queries
 * (e.g. "John 3:16") and free-text searches.
 *
 * All metadata imports are static — no dynamic imports inside this function.
 */
export async function searchBible(bibleData, term, versionId = null) {
  if (!term?.trim()) return [];

  const normalizedTerm = String(term).trim().replace(/\s+/g, " ");
  const q = normalizedTerm.toLowerCase();

  const referencePattern = /^(.+?)\s+(\d+):?(\d*)$/;
  const referenceMatch = normalizedTerm.match(referencePattern);
  let searchBook = null;
  let searchChapter = null;
  let searchVerse = null;

  if (referenceMatch) {
    searchBook = referenceMatch[1].trim().replace(/\s+/g, " ");
    searchChapter = referenceMatch[2];
    searchVerse = referenceMatch[3]?.trim() || null;
  }

  // Fast path: IndexedDB full-text search (non-reference queries only)
  if (!referenceMatch && versionId && isIndexedDBSupported()) {
    try {
      const results = await searchVersesInDB(versionId, term);
      if (results.length > 0) return results;
    } catch (err) {
      console.warn("[bibleCache] searchVersesInDB failed, falling back:", err);
    }
  }

  if (!bibleData) return [];

  const verses = bibleData.verses || bibleData;
  if (!Array.isArray(verses) || verses.length === 0) return [];

  let matchedBookNames = null;
  let extractedMetadata = null;

  // Pre-resolve which book names match the reference book query
  if (referenceMatch) {
    const lowerSearchBook = String(searchBook).toLowerCase().trim();
    matchedBookNames = new Set();

    try {
      extractedMetadata = await extractBookMetadata(bibleData, versionId);
    } catch (err) {
      console.warn("[bibleCache] Could not extract metadata for search:", err);
    }

    const allBookNames = new Set();
    for (const v of verses) {
      if (v && (v.book_name || v.book)) allBookNames.add(v.book_name || v.book);
    }

    for (const bookName of allBookNames) {
      const lowerBookName = String(bookName).toLowerCase().trim();
      if (lowerBookName.includes(lowerSearchBook) || lowerSearchBook.includes(lowerBookName)) {
        matchedBookNames.add(bookName);
        continue;
      }
      try {
        const matches = await bookMatchesQuery(bookName, searchBook, bibleData, versionId, extractedMetadata);
        if (matches) matchedBookNames.add(bookName);
      } catch (err) {
        console.warn("[bibleCache] bookMatchesQuery failed for:", bookName, err);
      }
    }
  }

  const MAX_RESULTS = 100;
  const results = [];

  for (let i = 0; i < verses.length && results.length < MAX_RESULTS; i++) {
    const v = verses[i];
    if (!v) continue;

    const bookName = v.book_name || v.book || "";
    const chapter = String(v.chapter || "");
    const verse = String(v.verse || "");
    const text = v.text || "";

    if (referenceMatch) {
      if (matchedBookNames && !matchedBookNames.has(bookName)) continue;
      if (chapter !== searchChapter && Number(chapter) !== Number(searchChapter)) continue;
      if (searchVerse && verse !== searchVerse && Number(verse) !== Number(searchVerse)) continue;

      results.push({
        book: bookName,
        chapter,
        verse,
        text,
        fullReference: `${bookName} ${chapter}:${verse}`
      });
      continue;
    }

    const searchText = `${bookName} ${chapter}:${verse} ${text}`.toLowerCase();
    if (searchText.includes(q)) {
      results.push({
        book: bookName,
        chapter,
        verse,
        text,
        fullReference: `${bookName} ${chapter}:${verse}`
      });
      continue;
    }

    try {
      const matches = await bookMatchesQuery(bookName, normalizedTerm, bibleData, versionId);
      if (matches) {
        results.push({
          book: bookName,
          chapter,
          verse,
          text,
          fullReference: `${bookName} ${chapter}:${verse}`
        });
      }
    } catch (err) {
      console.warn("[bibleCache] bookMatchesQuery failed during search:", err);
    }
  }

  return results;
}

// ─── Book / chapter / verse utilities ────────────────────────────────────────

export async function getAllBooks(bibleData, versionId = null) {
  if (!bibleData) return [];

  const verses = bibleData.verses || bibleData;
  if (!Array.isArray(verses)) return [];

  const uniqueBooks = [...new Set(verses.map((v) => v?.book_name || v?.book).filter(Boolean))];

  try {
    return await getOrderedBooks(uniqueBooks, bibleData, versionId);
  } catch (err) {
    console.error("[bibleCache] getAllBooks: getOrderedBooks failed:", err);
    return uniqueBooks.sort();
  }
}

export async function getChaptersForBook(bibleData, book, versionId = null) {
  if (!book) return [];

  if (versionId && isIndexedDBSupported()) {
    try {
      const chapters = await getChaptersFromDB(versionId, book);
      if (chapters.length > 0) return chapters;
    } catch (err) {
      console.warn("[bibleCache] getChaptersFromDB failed, falling back:", err);
    }
  }

  if (!bibleData) return [];
  const verses = bibleData.verses || bibleData;
  if (!Array.isArray(verses)) return [];

  return [...new Set(verses.filter((v) => v && (v.book_name || v.book) === book).map((v) => String(v.chapter)))].filter(Boolean).sort((a, b) => Number(a) - Number(b));
}

export async function getVersesForChapter(bibleData, book, chapter, versionId = null) {
  if (!book || !chapter) return [];

  if (versionId && isIndexedDBSupported()) {
    try {
      const verses = await getVersesFromDB(versionId, book, chapter);
      if (verses.length > 0) return verses;
    } catch (err) {
      console.warn("[bibleCache] getVersesFromDB failed, falling back:", err);
    }
  }

  if (!bibleData) return [];
  const verses = bibleData.verses || bibleData;
  if (!Array.isArray(verses)) return [];

  return verses
    .filter((v) => v && (v.book_name || v.book) === book && String(v.chapter) === String(chapter))
    .map((v) => String(v.verse))
    .filter(Boolean)
    .sort((a, b) => Number(a) - Number(b));
}

/**
 * Looks up the text of a specific verse by exact book name, chapter, and verse.
 * Callers should always pass the resolved book name for the active version.
 */
export async function getVerseByReference(bibleData, book, chapter, verse, versionId = null) {
  if (!book || !chapter || !verse) return "";

  if (versionId && isIndexedDBSupported()) {
    try {
      const text = await getVerseFromDB(versionId, book, chapter, verse);
      if (text) return text;
    } catch (err) {
      console.warn("[bibleCache] getVerseFromDB failed, falling back:", err);
    }
  }

  if (!bibleData) return "";
  const verses = bibleData.verses || bibleData;
  if (!Array.isArray(verses)) return "";

  const v = verses.find((x) => x && (x.book_name || x.book) === book && String(x.chapter) === String(chapter) && String(x.verse) === String(verse));
  return v?.text || "";
}
